import { getDocument } from '@atlaskit/browser-apis';

/**
 * Mirrors `graphql-ws`'s own randomised exponential backoff with a ceiling. The jitter is what
 * stops every client that dropped off one server from retrying in lockstep, and it is applied
 * before the cap so the cap is a true maximum.
 */
export const createRetryWait =
	(maxRetryWait: number) =>
	async (retries: number): Promise<void> => {
		const jitter = Math.floor(Math.random() * 2700 + 300);
		const delay = Math.min(1000 * 2 ** retries + jitter, maxRetryWait);
		await new Promise((resolve) => {
			setTimeout(resolve, delay);
		});
	};

/**
 * Whether a failed connection is worth another attempt.
 *
 * `graphql-ws` decides with `isLikeCloseEvent`, which needs both `code` and `reason` on the
 * payload. A socket-level failure has neither - `socket.onerror` delivers a bare event, and the
 * client prefers the error over the close that follows it - so the most ordinary failure there
 * is, the network being unreachable, was classified as permanent.
 *
 * A real `Error` keeps the library's answer, because those are its own internal failures and it
 * closes the socket with a fatal code for each. Nothing the library considers permanent is
 * widened: the fatal close codes and the retry budget are checked before this is consulted.
 */
export const shouldRetryTransportFailure = (errOrCloseEvent: unknown): boolean =>
	!(errOrCloseEvent instanceof Error);

/**
 * Forces a retry when the browser signals that the page or the network came back, and returns a
 * function that removes the listeners again.
 *
 * `online` and a tab becoming visible act only while disconnected, because terminating a healthy
 * socket on every tab switch would be worse than the problem being fixed. A back/forward-cache
 * restore or a resume from a frozen tab terminates unconditionally: timers were stopped and no
 * close was processed, so what the client believes about the socket cannot be trusted.
 */
export const addRecoveryListeners = ({
	terminate,
	isConnected,
}: {
	terminate: () => void;
	isConnected: () => boolean;
}): (() => void) => {
	const doc = getDocument();

	const wakeIfDisconnected = (): void => {
		if (!isConnected()) {
			terminate();
		}
	};

	const onVisibilityChange = (): void => {
		if (doc?.visibilityState === 'visible') {
			wakeIfDisconnected();
		}
	};

	const onPageShow = (event: PageTransitionEvent): void => {
		// Every ordinary load fires this too, and terminating there would close the socket the
		// page had just opened.
		if (event.persisted) {
			terminate();
		}
	};

	const onResume = (): void => {
		terminate();
	};

	window.addEventListener('online', wakeIfDisconnected);
	window.addEventListener('pageshow', onPageShow);
	doc?.addEventListener('visibilitychange', onVisibilityChange);
	// Chrome's resource-saving tab freezing, which stops timers the same way the back/forward
	// cache does but fires no `pageshow`.
	doc?.addEventListener('resume', onResume);

	return () => {
		window.removeEventListener('online', wakeIfDisconnected);
		window.removeEventListener('pageshow', onPageShow);
		doc?.removeEventListener('visibilitychange', onVisibilityChange);
		doc?.removeEventListener('resume', onResume);
	};
};
